import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Globe,
  Info,
  LogOut,
  ScrollText,
  UserCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useNavigationLanguage } from "../../hooks/useNavigationLanguage";
import { getLogoutUrl } from "../../utils/authentication";
import { getCurrentLanguageLabel, languages } from "../../utils/i18n";
import { AppInformationDialog } from "../NavigationUserMenu/components/AppInformationDialog";
import type { NavigationUser } from "../NavigationUserMenu/types";
import { getUserGroupLabel } from "../NavigationUserMenu/utils";

type SidebarUserMenuProps = {
  user?: NavigationUser;
  profilePath?: string;
};

const getInitials = (user?: NavigationUser) => {
  const words = user?.full_name?.split(/[\s,]+/).filter(Boolean) ?? [];
  const initials = words.slice(0, 2).map(word => word[0]);

  return (initials.length ? initials.join("") : (user?.username?.[0] ?? "")).toUpperCase();
};

export const SidebarUserMenu = ({ user, profilePath }: SidebarUserMenuProps) => {
  const { t } = useTranslation();
  const { isMobile, state } = useSidebar();
  const { handleLanguageChange, selectedLanguage } = useNavigationLanguage();
  const [view, setView] = useState<"main" | "language">("main");
  const [appInformationDialogOpen, setAppInformationDialogOpen] = useState(false);

  const isCollapsed = state === "collapsed" && !isMobile;
  const displayName = user?.full_name || user?.username || "";
  const groupLabel = getUserGroupLabel(user, t);
  const logoutUrl = getLogoutUrl(localStorage.getItem("ciam_id_token"));

  return (
    <>
      <SidebarMenu className="group-data-[collapsible=icon]:items-center">
        <SidebarMenuItem>
          <DropdownMenu onOpenChange={open => !open && setView("main")}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="group/user-menu h-14 gap-3 px-2 transition-[width,height,padding,background-color] duration-200 data-[state=open]:bg-sidebar-accent group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:bg-transparent! group-data-[collapsible=icon]:hover:bg-transparent! group-data-[collapsible=icon]:data-[state=open]:bg-transparent!"
              >
                <Avatar
                  size="lg"
                  className="ring-0 ring-primary-200 ring-offset-2 ring-offset-background transition-[box-shadow,transform] duration-200 ease-out group-hover/user-menu:scale-105 group-hover/user-menu:ring-2 group-focus-visible/user-menu:ring-2 group-data-[state=open]/user-menu:ring-2 group-data-[collapsible=icon]:size-10 motion-reduce:transition-none motion-reduce:group-hover/user-menu:scale-100"
                >
                  <AvatarFallback className="bg-primary-100 text-primary-700">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold text-base">{displayName}</span>
                  {groupLabel && (
                    <span className="truncate text-muted-foreground text-xs">{groupLabel}</span>
                  )}
                </span>
                <ChevronsUpDown className="ml-auto text-muted-foreground group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side={isCollapsed ? "right" : "top"}
              align={isCollapsed ? "end" : "start"}
              className="w-(--radix-dropdown-menu-trigger-width) min-w-60"
            >
              {view === "main" ? (
                <>
                  <DropdownMenuLabel className="flex flex-col gap-1">
                    <span className="truncate font-semibold">{displayName}</span>
                    <span className="truncate font-normal text-muted-foreground">
                      {user?.username}
                    </span>
                    {groupLabel && (
                      <span className="truncate font-normal text-muted-foreground">
                        {groupLabel}
                      </span>
                    )}
                  </DropdownMenuLabel>
                  {profilePath && (
                    <DropdownMenuItem asChild>
                      <Link to={profilePath} className="text-black">
                        <UserCircle />
                        {t("header.profile")}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onSelect={event => {
                      event.preventDefault();
                      setView("language");
                    }}
                  >
                    <Globe />
                    {t("header.language")}
                    <span className="ml-auto text-muted-foreground">
                      {getCurrentLanguageLabel(selectedLanguage)}
                    </span>
                    <ChevronRight />
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setAppInformationDialogOpen(true)}>
                    <Info />
                    {t("header.appInformation")}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      href="https://cdn.wfp.org/legal/terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black"
                    >
                      <ScrollText />
                      {t("common.termsAndConditions")}
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a href={logoutUrl} className="text-black">
                      <LogOut />
                      {t("header.logout")}
                    </a>
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem
                    onSelect={event => {
                      event.preventDefault();
                      setView("main");
                    }}
                    className="font-semibold"
                  >
                    <ChevronLeft />
                    {t("header.language")}
                  </DropdownMenuItem>
                  {languages.map(({ label, value }: { label: string; value: string }) => {
                    const isSelected = value === selectedLanguage;

                    return (
                      <DropdownMenuItem
                        key={value}
                        onSelect={() => handleLanguageChange(value)}
                        className="gap-3"
                      >
                        <span className="w-6 text-muted-foreground text-xs">
                          {value.split("_")[0].toUpperCase()}
                        </span>
                        <span className={cn(isSelected && "font-semibold")}>{label}</span>
                        {isSelected && <Check className="ml-auto text-primary" />}
                      </DropdownMenuItem>
                    );
                  })}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <AppInformationDialog
        open={appInformationDialogOpen}
        onOpenChange={setAppInformationDialogOpen}
      />
    </>
  );
};
